/**
 * AI 供应商管理（旧 views/system/AIProviderView.vue）：列表、默认供应商切换、新建编辑删除、连接测试与测试历史
 */
import { useEffect, useState } from 'react'
import { App, Badge, Button, Popconfirm, Space, Switch, Table, Typography, type TableColumnsType } from 'antd'
import { ApiOutlined, DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons'
import { PageHeader } from '@/components/PageHeader'
import { useDeleteProvider, useProviderList, useToggleDefaultProvider } from './hooks'
import { useProviderTesting } from './useProviderTesting'
import { ProviderFormModal } from './ProviderFormModal'
import { ProviderTestModal } from './ProviderTestModal'
import { TestHistoryCard } from './TestHistoryCard'
import type { AIProvider } from './types'
import styles from './AIProviderPage.module.css'

type FormState = { provider: AIProvider | null } | null

function CostCell({ provider }: Readonly<{ provider: AIProvider }>) {
  if (!provider.input_cost_per_million && !provider.output_cost_per_million) {
    return <Typography.Text type="secondary">未设置</Typography.Text>
  }
  const unit = ` ${provider.cost_currency || 'USD'}/M`
  return (
    <div>
      <div>
        <Typography.Text type="secondary">输入: </Typography.Text>
        {provider.input_cost_per_million || 0}
        <Typography.Text type="secondary">{unit}</Typography.Text>
      </div>
      <div>
        <Typography.Text type="secondary">输出: </Typography.Text>
        {provider.output_cost_per_million || 0}
        <Typography.Text type="secondary">{unit}</Typography.Text>
      </div>
    </div>
  )
}

export function AIProviderPage() {
  const { message } = App.useApp()
  const { data: providers, isFetching, isError, refetch } = useProviderList()
  const deleteProvider = useDeleteProvider()
  const toggleDefault = useToggleDefaultProvider()
  const testing = useProviderTesting()
  const [formState, setFormState] = useState<FormState>(null)
  const [testProvider, setTestProvider] = useState<AIProvider | null>(null)

  useEffect(() => {
    if (isError) message.error('获取供应商列表失败')
  }, [isError, message])

  const handleToggleDefault = async (record: AIProvider) => {
    try {
      await toggleDefault.mutateAsync(record.id)
      message.success(record.is_default ? '已取消默认供应商' : '已设为默认供应商')
    } catch (error: any) {
      console.error('切换默认供应商失败:', error)
      message.error(error?.response?.data?.detail || error?.message || '操作失败')
    }
  }

  const handleDelete = async (id: number) => {
    try {
      await deleteProvider.mutateAsync(id)
      message.success('删除成功')
    } catch (error) {
      console.error('删除失败:', error)
      message.error('删除失败')
    }
  }

  const columns: TableColumnsType<AIProvider> = [
    {
      title: '供应商',
      key: 'provider_name',
      render: (_, record) => (
        <div className={styles.providerName}>
          <span>{record.provider_name}</span>
          {record.is_active && <Badge status="success" text="当前使用" />}
        </div>
      )
    },
    {
      title: '模型信息',
      key: 'model_info',
      render: (_, record) => (
        <div>
          <div>{record.model_name}</div>
          <Typography.Text type="secondary" className={styles.small}>
            {record.api_base_url}
          </Typography.Text>
        </div>
      )
    },
    {
      title: '配置参数',
      key: 'config',
      render: (_, record) => (
        <Space orientation="vertical" size="small">
          <span>温度: {record.temperature}</span>
          <span>最大Token: {record.max_tokens}</span>
          <span>速率限制: {record.rate_limit}/分钟</span>
        </Space>
      )
    },
    { title: '成本', key: 'cost', render: (_, record) => <CostCell provider={record} /> },
    {
      title: '状态',
      key: 'status',
      width: 100,
      align: 'center',
      render: (_, record) => (
        <>
          <Switch
            aria-label={`${record.provider_name} 设为默认`}
            checked={record.is_default}
            disabled={!record.is_active}
            loading={toggleDefault.isPending && toggleDefault.variables === record.id}
            onChange={() => void handleToggleDefault(record)}
          />
          {!record.is_active && (
            <div>
              <Typography.Text type="secondary" className={styles.tiny}>
                非活跃
              </Typography.Text>
            </div>
          )}
        </>
      )
    },
    {
      title: '操作',
      key: 'action',
      width: 220,
      render: (_, record) => (
        <Space>
          <Button type="link" size="small" icon={<ApiOutlined />} onClick={() => setTestProvider(record)}>
            测试
          </Button>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => setFormState({ provider: record })}>
            编辑
          </Button>
          <Popconfirm
            title="确定要删除此供应商吗？"
            disabled={record.is_active}
            onConfirm={() => handleDelete(record.id)}
          >
            <Button type="link" danger size="small" icon={<DeleteOutlined />} disabled={record.is_active}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ]

  return (
    <div>
      <PageHeader title="AI供应商管理" subtitle="配置和管理AI语言模型供应商" />

      <div className={styles.actionBar}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setFormState({ provider: null })}>
          添加供应商
        </Button>
        <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
          刷新
        </Button>
      </div>

      <Table<AIProvider>
        rowKey="id"
        columns={columns}
        dataSource={providers}
        loading={isFetching}
        pagination={false}
        scroll={{ x: 1000 }}
      />

      <TestHistoryCard history={testing.history} onClear={testing.clearHistory} />

      <ProviderFormModal
        open={formState !== null}
        provider={formState?.provider ?? null}
        onClose={() => setFormState(null)}
      />
      <ProviderTestModal provider={testProvider} testing={testing} onClose={() => setTestProvider(null)} />
    </div>
  )
}
